/**
 * Ported from three/examples/jsm/lines/LineMaterial.js (MIT License).
 * Source: https://github.com/mrdoob/three.js/blob/master/examples/jsm/lines/
 *
 * Adapted to TypeScript:
 *   - Strict typing on constructor params and internal state
 *   - GLSL shader extracted to a separate `.glsl.ts` template literal module
 *   - `onUploadCallback` defensive null-ing (Opt 1a) added on instance buffers
 */

import * as THREE from "three";

import { FRAGMENT_SHADER } from "./LineMaterial.fragment.glsl";
import { VERTEX_SHADER } from "./LineMaterial.vertex.glsl";

// Mirrors the original three.js side-effect: register `line` uniforms + shader
// entry under the shared `UniformsLib` / `ShaderLib` namespaces so anything
// referencing `ShaderLib['line']` resolves regardless of import order.
//
// `UniformsLib` and `ShaderLib` are typed as `Record<string, ...>` upstream;
// the index assignment is intentional and matches the addon source exactly.
(THREE.UniformsLib as unknown as Record<string, Record<string, THREE.IUniform>>)[
  "line"
] = {
  worldUnits: { value: 1 },
  linewidth: { value: 1 },
  resolution: { value: new THREE.Vector2(1, 1) },
  dashOffset: { value: 0 },
  dashScale: { value: 1 },
  dashSize: { value: 1 },
  gapSize: { value: 1 }, // todo FIX - maybe change to totalSize
  // PR-LT3 variant A (A1) — multi-element pattern texture uniforms. Active
  // only under the `USE_DASH_TEXTURE` define; the single-pair `dashSize` /
  // `gapSize` path is unaffected when the define is absent.
  dashPatternTex: { value: null },
  dashPatternLength: { value: 1 },
  dashElementCount: { value: 0 },
};

interface ShaderLibEntry {
  uniforms: { [uniform: string]: THREE.IUniform };
  vertexShader: string;
  fragmentShader: string;
}

const _shaderLib = THREE.ShaderLib as unknown as Record<string, ShaderLibEntry>;
const _uniformsLib = THREE.UniformsLib as unknown as Record<
  string,
  { [uniform: string]: THREE.IUniform }
>;

_shaderLib["line"] = {
  uniforms: THREE.UniformsUtils.merge([
    _uniformsLib["common"],
    _uniformsLib["fog"],
    _uniformsLib["line"],
  ]),

  vertexShader: VERTEX_SHADER,
  fragmentShader: FRAGMENT_SHADER,
};

/**
 * Parameters accepted by LineMaterial constructor. Mirrors the loose JS object
 * that `ShaderMaterial.setValues` expects — any property of the parent material
 * may be passed.
 */
export interface LineMaterialParameters extends THREE.ShaderMaterialParameters {
  color?: THREE.ColorRepresentation;
  dashed?: boolean;
  dashScale?: number;
  dashSize?: number;
  dashOffset?: number;
  gapSize?: number;
  /**
   * PR-LT3 variant A (A1) — full multi-element pattern texture (from
   * `linetypePatternTexture.ts`). When set together with `dashElementCount > 0`
   * the material flips the `USE_DASH_TEXTURE` define and the fragment shader
   * walks the texture instead of the single `dashSize`/`gapSize` pair.
   */
  dashPatternTex?: THREE.Texture | null;
  /** A1 — pattern repeat period in drawing units (period for `vLineDistance`). */
  dashPatternLength?: number;
  /** A1 — element count = pattern texture width. `0` disables the texture path. */
  dashElementCount?: number;
  linewidth?: number;
  resolution?: THREE.Vector2;
  worldUnits?: boolean;
  alphaToCoverage?: boolean;
}

/**
 * A material for drawing wireframe-style geometries.
 *
 * Unlike {@link THREE.LineBasicMaterial}, it supports arbitrary line widths and
 * allows using world units instead of screen space units. This material is used
 * with {@link LineSegments2} and {@link Line2}.
 *
 * WebGLRenderer only.
 */
export class LineMaterial extends THREE.ShaderMaterial {
  /**
   * This flag can be used for type testing.
   */
  public readonly isLineMaterial = true as const;

  /**
   * Constructs a new line material.
   */
  constructor(parameters?: LineMaterialParameters) {
    super({
      uniforms: THREE.UniformsUtils.clone(_shaderLib["line"]!.uniforms),

      vertexShader: _shaderLib["line"]!.vertexShader,
      fragmentShader: _shaderLib["line"]!.fragmentShader,

      clipping: true, // required for clipping support
    });

    // `type` is set on the material object directly — the upstream JS passes it
    // through the ShaderMaterial constructor's parameters object, but the
    // current TypeScript definitions don't list `type` on ShaderMaterialParameters.
    this.type = "LineMaterial";

    if (parameters !== undefined) {
      this.setValues(parameters);
    }
  }

  /**
   * The material's color.
   */
  get color(): THREE.Color {
    return this.uniforms["diffuse"]!.value as THREE.Color;
  }

  set color(value: THREE.Color) {
    this.uniforms["diffuse"]!.value = value;
  }

  /**
   * Whether the material's sizes (width, dash gaps) are in world units.
   */
  get worldUnits(): boolean {
    return "WORLD_UNITS" in this.defines;
  }

  set worldUnits(value: boolean) {
    if ((value === true) !== this.worldUnits) {
      this.needsUpdate = true;
    }

    if (value === true) {
      this.defines["WORLD_UNITS"] = "";
    } else {
      delete this.defines["WORLD_UNITS"];
    }
  }

  /**
   * Controls line thickness in CSS pixel units when `worldUnits` is `false` (default),
   * or in world units when `worldUnits` is `true`.
   */
  get linewidth(): number {
    return this.uniforms["linewidth"]!.value as number;
  }

  set linewidth(value: number) {
    if (!this.uniforms["linewidth"]) return;
    this.uniforms["linewidth"].value = value;
  }

  /**
   * Whether the line is dashed, or solid.
   */
  get dashed(): boolean {
    return "USE_DASH" in this.defines;
  }

  set dashed(value: boolean) {
    if ((value === true) !== this.dashed) {
      this.needsUpdate = true;
    }

    if (value === true) {
      this.defines["USE_DASH"] = "";
    } else {
      delete this.defines["USE_DASH"];
    }
  }

  /**
   * The scale of the dashes and gaps.
   */
  get dashScale(): number {
    return this.uniforms["dashScale"]!.value as number;
  }

  set dashScale(value: number) {
    this.uniforms["dashScale"]!.value = value;
  }

  /**
   * The size of the dash.
   */
  get dashSize(): number {
    return this.uniforms["dashSize"]!.value as number;
  }

  set dashSize(value: number) {
    this.uniforms["dashSize"]!.value = value;
  }

  /**
   * Where in the dash cycle the dash starts.
   */
  get dashOffset(): number {
    return this.uniforms["dashOffset"]!.value as number;
  }

  set dashOffset(value: number) {
    this.uniforms["dashOffset"]!.value = value;
  }

  /**
   * The size of the gap.
   */
  get gapSize(): number {
    return this.uniforms["gapSize"]!.value as number;
  }

  set gapSize(value: number) {
    this.uniforms["gapSize"]!.value = value;
  }

  /**
   * PR-LT3 variant A (A1) — full multi-element pattern texture. Assigning a
   * non-null texture sets the `dashPatternTex` sampler uniform; the
   * `USE_DASH_TEXTURE` define is driven by `dashElementCount` (a non-empty
   * pattern), so set the count after the texture for a clean compile.
   */
  get dashPatternTex(): THREE.Texture | null {
    return this.uniforms["dashPatternTex"]!.value as THREE.Texture | null;
  }

  set dashPatternTex(value: THREE.Texture | null) {
    this.uniforms["dashPatternTex"]!.value = value;
  }

  /** A1 — pattern repeat period in drawing units. */
  get dashPatternLength(): number {
    return this.uniforms["dashPatternLength"]!.value as number;
  }

  set dashPatternLength(value: number) {
    this.uniforms["dashPatternLength"]!.value = value;
  }

  /**
   * A1 — element count = pattern texture width. A positive count flips the
   * `USE_DASH_TEXTURE` define (the fragment shader walks the texture); `0`
   * removes the define (single-pair `dashSize`/`gapSize` path). Toggling the
   * define forces a shader recompile via `needsUpdate`.
   */
  get dashElementCount(): number {
    return this.uniforms["dashElementCount"]!.value as number;
  }

  set dashElementCount(value: number) {
    this.uniforms["dashElementCount"]!.value = value;
    const useTexture = value > 0;
    if (useTexture !== "USE_DASH_TEXTURE" in this.defines) {
      this.needsUpdate = true;
    }
    if (useTexture) {
      this.defines["USE_DASH_TEXTURE"] = "";
    } else {
      delete this.defines["USE_DASH_TEXTURE"];
    }
  }

  /**
   * PR-LT3 variant A (A2) — enable the per-instance CELTSCALE term in the
   * vertex shader (`instanceLtScale` attribute). When `true` the
   * `USE_INSTANCE_LTSCALE` define is added and `vLineDistance` is multiplied
   * by the geometry's per-segment `instanceLtScale` (1 / CELTSCALE). The
   * geometry MUST carry an `instanceLtScale` instanced attribute when this is
   * set — `buildSingleBucketMesh` assigns it before constructing the mesh.
   * When `false` (default) the define is absent and the shader uses
   * `dashScale` alone, so legacy / attribute-less geometry renders unchanged.
   */
  get instanceLtScaleEnabled(): boolean {
    return "USE_INSTANCE_LTSCALE" in this.defines;
  }

  set instanceLtScaleEnabled(value: boolean) {
    if (value !== this.instanceLtScaleEnabled) {
      this.needsUpdate = true;
    }
    if (value) {
      this.defines["USE_INSTANCE_LTSCALE"] = "";
    } else {
      delete this.defines["USE_INSTANCE_LTSCALE"];
    }
  }

  /**
   * The opacity.
   */
  override get opacity(): number {
    return this.uniforms["opacity"]!.value as number;
  }

  override set opacity(value: number) {
    if (!this.uniforms) return;
    if (!this.uniforms["opacity"]) return;
    this.uniforms["opacity"].value = value;
  }

  /**
   * The size of the viewport, in screen pixels. This must be kept updated to make
   * screen-space rendering accurate. The `LineSegments2.onBeforeRender` callback
   * performs the update for visible objects.
   */
  get resolution(): THREE.Vector2 {
    return this.uniforms["resolution"]!.value as THREE.Vector2;
  }

  set resolution(value: THREE.Vector2) {
    (this.uniforms["resolution"]!.value as THREE.Vector2).copy(value);
  }

  /**
   * Whether to use alphaToCoverage or not. When enabled, this can improve the
   * anti-aliasing of line edges when using MSAA.
   */
  override get alphaToCoverage(): boolean {
    return "USE_ALPHA_TO_COVERAGE" in this.defines;
  }

  override set alphaToCoverage(value: boolean) {
    if (!this.defines) return;

    if ((value === true) !== this.alphaToCoverage) {
      this.needsUpdate = true;
    }

    if (value === true) {
      this.defines["USE_ALPHA_TO_COVERAGE"] = "";
    } else {
      delete this.defines["USE_ALPHA_TO_COVERAGE"];
    }
  }
}
